// Modules
import cn from 'classnames';

// Types
import type { TTextAreaProps, TTextAreaStatus } from './TextArea.type';

// Styles
import styles from './styles/styles.module.scss';

// Components
import Icon from '../Icon';

// Helpers
import { generateID } from '../../helpers/common';

// Constants
import { TEXT_AREA_DEFAULT_PROPS, TEXT_AREA_STATUS, TEXT_AREA_VARIANT } from './TextArea.constant';
import { ICON_NAME } from '../Icon/Icon.constant';

const getCaptionIcon = (status: TTextAreaStatus) => {
	switch (status) {
		case TEXT_AREA_STATUS.error:
			return ICON_NAME['error-triangle'];
		case TEXT_AREA_STATUS.success:
			return ICON_NAME['success-circle'];
		default:
			return ICON_NAME['info-circle'];
	}
};

const TextArea = (props: TTextAreaProps) => {
	const {
		block,
		caption,
		captionClassName,
		className,
		disabled,
		hint,
		hintClassName,
		id,
		inputClassName,
		label,
		labelClassName,
		placeholder,
		size = TEXT_AREA_DEFAULT_PROPS.size,
		status = TEXT_AREA_DEFAULT_PROPS.status,
		variant = TEXT_AREA_DEFAULT_PROPS.variant,
		...restProps
	} = props;
	const isOutline = variant === TEXT_AREA_VARIANT.outline;

	const textAreaCN = cn(styles.legion_text_area, styles[size!], styles[status!], className, {
		[styles.block]: block,
		[styles.disabled]: disabled,
		[styles[variant!]]: variant,
	});
	const captionCN = cn(styles.text_area_caption, captionClassName);
	const hintCN = cn(styles.text_area_hint, hintClassName);
	const inputCN = cn(styles.input_wrapper, inputClassName);
	const labelCN = cn(styles.text_area_label, labelClassName);

	const inputId = id || `legion_text_area_${generateID()}`;
	const captionIcon = getCaptionIcon(status!);

	return (
		<div className={textAreaCN}>
			{label && (
				<label
					htmlFor={inputId}
					className={labelCN}
				>
					{label}
				</label>
			)}
			<div className={inputCN}>
				<textarea
					className={styles.text_area_input}
					id={inputId}
					placeholder={placeholder}
					disabled={disabled}
					{...restProps}
				/>
				{isOutline && (
					<svg
						xmlns={'http://www.w3.org/2000/svg'}
					>
						<rect height={'100%'} width={'100%'} strokeWidth={'2px'} fill={'none'}/>
					</svg>
				)}
			</div>
			{caption && (
				<p className={captionCN}>
					<Icon className={styles.caption_icon} icon={captionIcon}/>
					{caption}
				</p>
			)}
			{hint && (
				<p className={hintCN}>
					{hint}
				</p>
			)}
		</div>
	);
}

export default TextArea;
